import { useState, useEffect, useMemo } from 'react';
import { MINI_GAMES } from './games.js';

const C = {
  bg:'#0a0a0a', card:'#161616', cardHi:'#1f1f1f', border:'#2a2a2a',
  text:'#fafafa', muted:'#a1a1aa', dim:'#71717a',
  alex:'#ef4444', group:'#f5c518', win:'#22c55e', multi:'#8b5cf6',
  red:'#ef4444', yellow:'#fbbf24',
};

// ─── Outgoing challenge modal ──────────────────────────────────────────────
export function ChallengeModal({ open, onClose, presence, me, send }) {
  const [gameType, setGameType] = useState('tic-tac-toe');
  if (!open) return null;
  const opponents = presence.filter(p => p.userId !== me.userId);
  return (
    <ModalShell title="Mini-Spiel · Herausfordern" onClose={onClose}>
      <div className="space-y-3">
        <div className="text-xs" style={{ color: C.muted }}>Wähle ein Spiel und einen Gegner. Beide müssen online sein.</div>
        <div className="grid grid-cols-3 gap-2">
          {MINI_GAMES.map(g => (
            <button key={g.type} onClick={() => setGameType(g.type)}
              className="rounded-lg p-2 text-center active:scale-95"
              style={{ background: gameType === g.type ? C.cardHi : C.card, border: `1px solid ${gameType === g.type ? C.multi : C.border}` }}>
              <div className="text-2xl">{g.icon}</div>
              <div className="text-[10px] font-bold mt-1">{g.name}</div>
            </button>
          ))}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Gegner</div>
          <div className="space-y-1.5 max-h-72 overflow-auto">
            {opponents.length === 0 && <div className="text-xs" style={{ color: C.muted }}>Niemand sonst online.</div>}
            {opponents.map(p => (
              <button key={p.userId} disabled={!p.active}
                onClick={() => { send({ type: 'challenge-create', toUserId: p.userId, gameType }); onClose(); }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg active:scale-95 disabled:opacity-40"
                style={{ background: C.cardHi, border: `1px solid ${C.border}` }}>
                <span className="w-2 h-2 rounded-full" style={{ background: p.active ? C.win : C.dim }} />
                <span className="flex-1 text-left font-bold text-sm">{p.name}</span>
                <span className="text-[10px] uppercase tracking-widest" style={{ color: p.role === 'leader' ? C.alex : p.role === 'alex' ? C.alex : C.group }}>{p.role}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

// ─── Incoming challenge toasts ─────────────────────────────────────────────
export function ChallengeInbox({ challenges, me, send }) {
  const incoming = challenges.filter(c => c.toUserId === me.userId);
  if (!incoming.length) return null;
  return (
    <div className="fixed bottom-4 left-2 right-2 z-30 space-y-2">
      {incoming.map(ch => {
        const g = MINI_GAMES.find(x => x.type === ch.gameType);
        return (
          <div key={ch.id} className="rounded-2xl p-3 flex items-center gap-3 shadow-xl"
            style={{ background: C.card, border: `1px solid ${C.multi}` }}>
            <div className="text-3xl">{g?.icon || '🎮'}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest" style={{ color: C.multi }}>Herausforderung</div>
              <div className="text-sm font-bold truncate">{ch.fromName} fordert dich zu {g?.name || ch.gameType}</div>
            </div>
            <button onClick={() => send({ type: 'challenge-respond', challengeId: ch.id, accepted: false })}
              className="rounded-md px-3 py-2 text-xs font-bold" style={{ background: C.cardHi, color: C.muted, border: `1px solid ${C.border}` }}>Nein</button>
            <button onClick={() => send({ type: 'challenge-respond', challengeId: ch.id, accepted: true })}
              className="rounded-md px-3 py-2 text-xs font-bold" style={{ background: C.win, color: '#000' }}>Annehmen</button>
          </div>
        );
      })}
    </div>
  );
}

// ─── Active mini-games (Spielbrett) ────────────────────────────────────────
export function MiniGamesPanel({ miniGames, me, send }) {
  const myGames = Object.values(miniGames).filter(g => g.players.some(p => p.userId === me.userId));
  if (!myGames.length) return null;
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center p-3" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-sm">
        {myGames.map(g => <MiniGameBoard key={g.id} game={g} me={me} send={send} />)}
      </div>
    </div>
  );
}

function MiniGameBoard({ game, me, send }) {
  const opp = game.players.find(p => p.userId !== me.userId);
  return (
    <div className="rounded-2xl p-4 space-y-3" style={{ background: C.card, border: `1px solid ${C.multi}` }}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.multi }}>
          {game.type === 'tic-tac-toe' ? 'Tic-Tac-Toe' : game.type === 'connect4' ? '4 Gewinnt' : 'Schnick-Schnack-Schnuck'} · vs. {opp?.name}
        </div>
        <button onClick={() => send({ type: 'mini-close', gameId: game.id })}
          className="text-xs px-2 py-1 rounded-md" style={{ color: C.muted, background: C.cardHi }}>Schließen</button>
      </div>
      {game.type === 'tic-tac-toe' && <TicTacToe game={game} me={me} send={send} />}
      {game.type === 'connect4'    && <Connect4    game={game} me={me} send={send} />}
      {game.type === 'ssp'         && <SchnickSchnackSchnuck game={game} me={me} send={send} />}
      {game.state.winner && (
        <button onClick={() => send({ type: 'mini-rematch', gameId: game.id })}
          className="w-full rounded-xl px-4 py-3 font-bold active:scale-95"
          style={{ background: C.win, color: '#000' }}>Revanche</button>
      )}
    </div>
  );
}

// ─── Tic-Tac-Toe ──────────────────────────────────────────────────────────
function TicTacToe({ game, me, send }) {
  const g = game.state;
  const isMyTurn = g.turn === me.userId && !g.winner;
  const myMark = g.mark[me.userId];
  const oppMark = Object.entries(g.mark).find(([u]) => u !== me.userId)?.[1];
  return (
    <div className="space-y-3">
      <div className="text-xs text-center" style={{ color: C.muted }}>
        Du bist <span className="font-bold" style={{ color: myMark === 'x' ? C.alex : C.group }}>{myMark.toUpperCase()}</span>
        {' · '}
        {g.winner ? (g.winner === 'tie' ? 'Unentschieden' : g.winner === me.userId ? '🏆 Du hast gewonnen!' : '😬 Verloren') : isMyTurn ? 'Du bist dran' : 'Warten…'}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {g.board.map((cell, i) => {
          const isWinCell = g.winLine?.includes(i);
          return (
            <button key={i} disabled={!isMyTurn || !!cell}
              onClick={() => send({ type: 'mini-move', gameId: game.id, idx: i })}
              className="aspect-square rounded-lg flex items-center justify-center text-4xl font-black disabled:opacity-80 active:scale-95"
              style={{
                background: isWinCell ? C.win : C.cardHi,
                color: cell === 'x' ? C.alex : cell === 'o' ? C.group : C.dim,
                border: `1px solid ${isWinCell ? C.win : C.border}`,
                fontFamily: 'Anton, sans-serif',
              }}>
              {cell ? cell.toUpperCase() : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── 4 Gewinnt ────────────────────────────────────────────────────────────
const C4_COLS = 7, C4_ROWS = 6;
function Connect4({ game, me, send }) {
  const g = game.state;
  const isMyTurn = g.turn === me.userId && !g.winner;
  const myColor = g.mark[me.userId];
  const colorOf = (mark) => mark === 'r' ? C.red : mark === 'y' ? C.yellow : null;
  const isWinCell = (col, row) => g.winCells?.some(([c, r]) => c === col && r === row);
  return (
    <div className="space-y-3">
      <div className="text-xs text-center" style={{ color: C.muted }}>
        Du bist <span className="font-bold" style={{ color: colorOf(myColor) }}>{myColor === 'r' ? 'ROT' : 'GELB'}</span>
        {' · '}
        {g.winner ? (g.winner === 'tie' ? 'Unentschieden' : g.winner === me.userId ? '🏆 Du hast gewonnen!' : '😬 Verloren') : isMyTurn ? 'Du bist dran' : 'Warten…'}
      </div>
      <div className="rounded-lg p-1.5" style={{ background: '#1e3a8a' }}>
        <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${C4_COLS}, 1fr)` }}>
          {Array.from({ length: C4_ROWS }).map((_, r) => (
            Array.from({ length: C4_COLS }).map((_, c) => {
              const v = g.board[r * C4_COLS + c];
              return (
                <button key={r * C4_COLS + c} disabled={!isMyTurn || !!v}
                  onClick={() => send({ type: 'mini-move', gameId: game.id, col: c })}
                  className="aspect-square rounded-full active:scale-95"
                  style={{
                    background: v ? colorOf(v) : '#0a1a3a',
                    border: isWinCell(c, r) ? `2px solid ${C.win}` : 'none',
                  }} />
              );
            })
          ))}
        </div>
      </div>
      {!g.winner && (
        <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${C4_COLS}, 1fr)` }}>
          {Array.from({ length: C4_COLS }).map((_, c) => (
            <button key={c} disabled={!isMyTurn}
              onClick={() => send({ type: 'mini-move', gameId: game.id, col: c })}
              className="rounded-md py-1 text-[10px] font-bold active:scale-95 disabled:opacity-30"
              style={{ background: C.cardHi, color: C.muted, border: `1px solid ${C.border}` }}>↓</button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Schnick-Schnack-Schnuck ──────────────────────────────────────────────
function SchnickSchnackSchnuck({ game, me, send }) {
  const g = game.state;
  const myChoice = g.choices?.[me.userId];
  const oppId = g.players.find(u => u !== me.userId);
  const oppChose = !!g.choices?.[oppId];
  const lastRound = g.rounds[g.rounds.length - 1];
  const myScore = g.scores[me.userId];
  const oppScore = g.scores[oppId];
  const icons = { stone: '✊', paper: '🖐️', scissors: '✌️' };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-around">
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: C.muted }}>Du</div>
          <div className="text-3xl font-black" style={{ color: C.win, fontFamily: 'Anton, sans-serif' }}>{myScore}</div>
        </div>
        <div className="text-xs opacity-30">VS</div>
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: C.muted }}>{g.names[oppId]}</div>
          <div className="text-3xl font-black" style={{ color: C.alex, fontFamily: 'Anton, sans-serif' }}>{oppScore}</div>
        </div>
      </div>
      {lastRound && !g.winner && (
        <div className="text-center text-xs" style={{ color: C.muted }}>
          Letzte Runde: {icons[lastRound[me.userId]]} vs. {icons[lastRound[oppId]]} · {lastRound.winner === 'tie' ? 'unentschieden' : lastRound.winner === me.userId ? 'du' : g.names[oppId]}
        </div>
      )}
      {g.winner ? (
        <div className="text-center py-2">
          <div className="text-3xl font-black" style={{ color: g.winner === me.userId ? C.win : C.alex }}>
            {g.winner === me.userId ? '🏆 GEWONNEN' : '😬 VERLOREN'}
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            {['stone', 'paper', 'scissors'].map(c => (
              <button key={c} disabled={!!myChoice}
                onClick={() => send({ type: 'mini-move', gameId: game.id, choice: c })}
                className="aspect-square rounded-xl flex items-center justify-center text-4xl active:scale-95 disabled:opacity-50"
                style={{ background: myChoice === c ? C.win : C.cardHi, border: `1px solid ${myChoice === c ? C.win : C.border}` }}>
                {icons[c]}
              </button>
            ))}
          </div>
          <div className="text-center text-xs" style={{ color: C.muted }}>
            {myChoice ? (oppChose ? 'Auswerten…' : `Du wartest auf ${g.names[oppId]}…`) : 'Wähle Stein, Papier oder Schere'}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Modal shell ──────────────────────────────────────────────────────────
function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={onClose}>
      <div className="w-full sm:max-w-md max-h-[88vh] overflow-auto rounded-t-2xl sm:rounded-2xl"
        style={{ background: C.card, border: `1px solid ${C.border}` }}
        onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 flex items-center justify-between px-4 py-3 border-b" style={{ background: C.card, borderColor: C.border }}>
          <div className="text-xs uppercase tracking-widest font-bold" style={{ color: C.muted }}>{title}</div>
          <button onClick={onClose} className="text-lg leading-none" style={{ color: C.dim }}>×</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
